// Minimal P-O-S Industry card for Super Admin (Phase 3): industry, lock state, pre-lock / privileged correction. No redesign.
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { SaHelperName } from "./SaHelperName";

export function SaIndustryCard({ clientId }) {
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(false);
  const [key, setKey] = useState("");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/superadmin/clients/${clientId}/industry`).then((r) => { setView(r.data); setKey(r.data.industry_key || ""); }).catch(() => setView(null));
  useEffect(() => { if (clientId) { setEdit(false); setReason(""); setConfirm(false); load(); } }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!view || !view.is_pos) return null;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/superadmin/clients/${clientId}/industry`, { industry_key: key, reason, confirm }); setView(data); setEdit(false); setReason(""); setConfirm(false); toast.success("Industry updated"); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.message || "Industry change failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-border p-3 mb-3 space-y-2" data-testid="sa-industry-card">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Industry Profile</p>
          <p className="font-medium text-sm" data-testid="sa-industry-name">{view.industry_name || "Not assigned"}</p>
        </div>
        <Badge variant="outline" className={view.locked ? "text-amber-600 border-amber-500/40" : "text-emerald-600 border-emerald-500/40"} data-testid="sa-industry-lock">{view.locked ? "Locked" : "Not locked"}</Badge>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="sa-industry-lock-reason">
        {view.locked ? `Locked by operational history: ${view.lock_triggers.map((t) => `${t.kind} (${t.count})`).join(", ")}. Industry is permanent; a different business type needs a new tenant.`
          : "Not locked yet — industry can be corrected until the first committed sale, refund, stock adjustment, A/R payment or Wallet entry."}
        {view.locked && view.internal_or_sandbox && " Internal Live Test / sandbox tenant: a privileged, audited correction is permitted."}
      </p>
      {view.industry_key && <SaHelperName profile={view.profiles.find((p) => p.key === view.industry_key)} onSaved={load} />}
      {view.correction_allowed && !edit && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="sa-industry-edit-btn">Correct industry</Button>}
      {edit && (
        <div className="space-y-2" data-testid="sa-industry-form">
          <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" value={key} onChange={(e) => setKey(e.target.value)} data-testid="sa-industry-select">
            {view.profiles.map((p) => <option key={p.key} value={p.key} disabled={p.active === false}>{p.name}{p.active === false ? " — Coming soon" : ""}</option>)}
          </select>
          <Input placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sa-industry-reason" />
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="sa-industry-confirm" /> I confirm this industry change{view.locked ? " on an internal/sandbox tenant with existing history" : ""}.</label>
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || !confirm || reason.trim().length < 5} onClick={save} data-testid="sa-industry-save">Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEdit(false)} data-testid="sa-industry-cancel">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
